import { useI18n } from "@/i18n/I18nProvider";
import { cn } from "@/lib/utils";
import { Check } from "lucide-react";
import { useEffect, useState } from "react";
import cartoonImg from "@/assets/style-cartoon.jpg";
import animeImg from "@/assets/style-anime.jpg";
import disneyImg from "@/assets/style-disney.jpg";
import comicImg from "@/assets/style-comic.jpg";
import neonImg from "@/assets/style-neon.jpg";
import ghibliImg from "@/assets/style-ghibli.jpg";
import oilImg from "@/assets/style-oil.jpg";
import headshotImg from "@/assets/style-headshot.jpg";
import caricatureImg from "@/assets/style-caricature.jpg";
import sketchImg from "@/assets/style-sketch.jpg";
import popartImg from "@/assets/style-popart.jpg";
import vintageImg from "@/assets/style-vintage.jpg";
import watercolorImg from "@/assets/style-watercolor.jpg";
import artNouveauImg from "@/assets/style-art-nouveau.jpg";
import lowPolyImg from "@/assets/style-low-poly.jpg";
import clay3dImg from "@/assets/style-clay-3d.jpg";

export type StyleId =
  | "neon"
  | "anime"
  | "comic"
  | "cartoon"
  | "ghibli"
  | "oil"
  | "disney"
  | "headshot"
  | "caricature"
  | "pencil"
  | "vintage"
  | "popart"
  | "watercolor"
  | "art_nouveau"
  | "low_poly"
  | "stained_glass";

const STYLES: { id: StyleId; image: string }[] = [
  { id: "neon", image: neonImg },
  { id: "anime", image: animeImg },
  { id: "comic", image: comicImg },
  { id: "cartoon", image: cartoonImg },
  { id: "ghibli", image: ghibliImg },
  { id: "oil", image: oilImg },
  { id: "disney", image: disneyImg },
  { id: "headshot", image: headshotImg },
  { id: "caricature", image: caricatureImg },
];

const ALL_STYLES: { id: StyleId; image: string; badge?: "new" }[] = [
  { id: "neon", image: neonImg },
  { id: "anime", image: animeImg },
  { id: "comic", image: comicImg },
  { id: "cartoon", image: cartoonImg },
  { id: "ghibli", image: ghibliImg },
  { id: "oil", image: oilImg },
  { id: "disney", image: disneyImg },
  { id: "headshot", image: headshotImg },
  { id: "caricature", image: caricatureImg, badge: "new" },
  { id: "pencil", image: sketchImg, badge: "new" },
  { id: "popart", image: popartImg, badge: "new" },
  { id: "vintage", image: vintageImg, badge: "new" },
  { id: "watercolor", image: watercolorImg, badge: "new" },
  { id: "art_nouveau", image: artNouveauImg, badge: "new" },
  { id: "low_poly", image: lowPolyImg, badge: "new" },
  { id: "stained_glass", image: clay3dImg, badge: "new" },
];

const STYLE_TIPS: Record<
  StyleId,
  { title: string; tips: string[]; text: string }
> = {
  neon: {
    title: "⚡ Best for Neon",
    tips: [
      "Clear face photos",
      "Close portraits",
      "Good lighting",
      "People and pets supported",
    ],
    text: "Creates the strongest cyberpunk glow with sharp, detailed photos. Best results come from clear faces and high-quality images.",
  },
  anime: {
    title: "🌸 Best for Anime / Manga",
    tips: [
      "Single portraits",
      "Clear eyes and faces",
      "Good quality photos",
      "People and pets supported",
    ],
    text: "Transforms photos into anime art while keeping identity. Clear subjects give the cleanest results.",
  },
  comic: {
    title: "💥 Best for Comic Book",
    tips: [
      "People, families and couples",
      "Pets of all types",
      "Strong expressions",
      "Clear subject",
    ],
    text: "Creates a bold superhero comic effect while preserving important details, colors and personality.",
  },
  cartoon: {
    title: "🎨 Best for Cartoon Classic",
    tips: [
      "Kids and families",
      "Pets",
      "Full body photos",
      "Lifestyle moments",
    ],
    text: "Turns memories into fun cartoon illustrations while keeping the original scene, colors and details.",
  },
  ghibli: {
    title: "🌿 Best for Ghibli",
    tips: [
      "Family memories",
      "Couples",
      "Pets",
      "Nature and travel photos",
    ],
    text: "Creates a warm magical storybook feeling. Works especially well with emotional photos and meaningful backgrounds.",
  },
  oil: {
    title: "🖼️ Best for Oil Painting",
    tips: [
      "Close portraits",
      "Pets",
      "High quality photos",
      "Gift and wall art images",
    ],
    text: "Creates a timeless painted portrait look with premium canvas feeling. Best with detailed, well-lit photos.",
  },
  disney: {
    title: "✨ Best for Disney / Pixar",
    tips: [
      "Kids and families",
      "Couples",
      "Pets",
      "Clear faces",
    ],
    text: "Creates a premium 3D animated movie style while preserving the original character and emotion.",
  },
  headshot: {
    title: "📸 Best for Headshot",
    tips: [
      "Single person portraits",
      "Pet portraits",
      "Clear face and eyes",
      "Good lighting",
    ],
    text: "Transforms normal photos into professional studio-quality portraits while keeping a realistic look.",
  },
  caricature: {
    title: "😄 Best for Caricature",
    tips: ["Close-up portraits", "Clear faces", "Good lighting", "People and pets"],
    text: "Best results with close-up photos that show clear facial features and expressions.",
  },
  pencil: {
    title: "✏️ Best for Pencil Sketch",
    tips: ["Clear portraits", "Good lighting", "Sharp facial detail", "People and pets"],
    text: "Creates a museum-quality hand-drawn graphite pencil portrait while preserving identity.",
  },
  vintage: {
    title: "📰 Best for Vintage Comic",
    tips: ["Clear portraits", "Good lighting", "Strong expressions", "People and pets"],
    text: "Creates an authentic Golden Age comic book illustration while preserving identity and facial structure.",
  },
  popart: {
    title: "🎨 Best for Pop Art",
    tips: ["Portraits", "Pets", "Colorful photos", "Bold compositions"],
    text: "Best with portraits, pets and colorful photos.",
  },
  watercolor: {
    title: "🎨 Best for Watercolor",
    tips: ["Portraits", "Soft lighting", "Clear faces", "People and pets"],
    text: "Creates a museum-quality hand-painted watercolor portrait while preserving identity.",
  },
  art_nouveau: {
    title: "🌿 Best for Art Nouveau",
    tips: ["Portraits", "Elegant poses", "Clear faces", "People"],
    text: "Creates a Mucha-inspired decorative poster with flowing linework and ornamental frames.",
  },
  low_poly: {
    title: "🔷 Best for Low Poly",
    tips: ["Portraits", "Clear faces", "Good lighting", "Bold compositions"],
    text: "Creates a modern geometric polygon portrait with clean triangular facets.",
  },
  stained_glass: {
    title: "🏺 Best for Clay 3D",
    tips: ["Portraits", "Clear faces", "Good lighting", "People and pets"],
    text: "Creates a sculptural clay 3D portrait with smooth matte textures and warm earthy tones.",
  },
};

const TipsCard = ({ selected }: { selected: StyleId }) => {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setShow(true), 30);
    return () => clearTimeout(t);
  }, []);

  const data = STYLE_TIPS[selected];
  return (
    <div
      className={cn(
        "transition-all duration-500 ease-out",
        show ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"
      )}
    >
      <div className="rounded-2xl border border-neon-green/20 bg-background/50 backdrop-blur-md p-4 md:p-5 shadow-[0_0_20px_-5px_hsl(var(--neon-green)/0.12)]">
        <h3 className="font-display font-bold text-base md:text-lg text-gradient mb-3">
          {data.title}
        </h3>
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-y-1.5 gap-x-4 mb-3">
          {data.tips.map((tip) => (
            <li
              key={tip}
              className="flex items-center gap-2 text-xs md:text-sm text-foreground/90"
            >
              <Check className="w-3.5 h-3.5 text-neon-green shrink-0" />
              {tip}
            </li>
          ))}
        </ul>
        <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">
          {data.text}
        </p>
      </div>
    </div>
  );
};

export const StyleSelector = ({
  selected,
  onSelect,
}: {
  selected: StyleId;
  onSelect: (s: StyleId) => void;
}) => {
  const { t } = useI18n();


  return (
    <section id="styles" className="py-12 md:py-20 relative">
      <div className="container">
        <div className="text-center mb-6 md:mb-10 space-y-2">
          <h2 className="font-display text-3xl md:text-5xl font-black">
            <span className="text-gradient">{t("styles.title")}</span>
          </h2>
          <p className="text-gradient text-sm md:text-lg">{t("styles.subtitle")}</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3 max-w-md md:max-w-4xl mx-auto">
          {ALL_STYLES.map((s) => {
            const active = selected === s.id;
            return (
              <button
                key={s.id}
                onClick={() => onSelect(s.id)}
                className={cn(
                  "group relative aspect-square md:aspect-[4/5] rounded-2xl overflow-hidden border-2 transition-all duration-300",
                  active
                    ? "border-neon-green glow-green scale-[1.02]"
                    : "border-border hover:border-neon-purple/60"
                )}
              >
                <img
                  src={s.image}
                  alt={`${t(`style.${s.id}`)} cartoon portrait example`}
                  loading="lazy"
                  width={512}
                  height={640}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/30 to-transparent" />
                <div className="absolute bottom-0 inset-x-0 p-2 md:p-4 text-left">
                  <div className="font-display font-bold text-[11px] md:text-lg leading-tight">{t(`style.${s.id}`)}</div>
                  <div className="text-[9px] md:text-xs text-muted-foreground leading-tight mt-0.5 line-clamp-2">{t(`style.${s.id}.desc`)}</div>
                </div>
                {s.badge === "new" && (
                  <div className="absolute top-3 left-3 px-2 py-0.5 rounded-full bg-neon-green text-[10px] md:text-xs font-black text-background uppercase tracking-wider shadow-[0_0_10px_hsl(var(--neon-green)/0.6)]">
                    {t("badge.new")}
                  </div>
                )}
                {active && (
                  <div className="absolute top-3 right-3 w-7 h-7 rounded-full bg-neon-green grid place-items-center text-background font-black">
                    ✓
                  </div>
                )}
              </button>
            );
          })}
        </div>
        <div className="mt-6 md:mt-8 max-w-md md:max-w-4xl mx-auto">
          <TipsCard key={selected} selected={selected} />
        </div>

      </div>
    </section>
  );
};

export { STYLES, ALL_STYLES };
